import { Autocomplete, Box, Chip, Grid, TextField } from "@mui/material";
import { type FocusEventHandler, useState } from "react";

export function MultiTextFormFieldControlledComponent({
  disabled,
  error,
  onBlur,
  onChange,
  name,
  value,
  items,
  showMoreIndicator = true,
}: {
  disabled?: boolean;
  error?: boolean;
  max?: number;
  onBlur: FocusEventHandler<HTMLInputElement>;
  onChange: (event: { target: { name: string; value: string[] } }) => void;
  name: string;
  value?: string[];
  items?: { label: string; value: string }[];
  showMoreIndicator?: boolean; // Optional prop for showing "+ n more" (applied only when items are provided)
}) {
  const [inputValue, setInputValue] = useState("");
  const [values, setValues] = useState(value ?? []);

  if (!Array.isArray(values)) {
    throw new Error(
      `A non-array value was handed over to MultiTextFormField for ${name}.`
    );
  }

  function handleValueChanges(newValues: string[]) {
    if (disabled) return;

    const validValues = newValues.filter((val) => val.trim() !== "");
    setValues(validValues);
    onChange({
      target: {
        name: name,
        value: validValues,
      },
    });
  }

  function addValue(value: string) {
    if (disabled || value.trim() === "") return;
    const newValues = [...values, value];
    handleValueChanges(newValues);
    setInputValue("");
  }

  function removeValue(index: number) {
    if (disabled) return;
    const newValues = values.filter((_, i) => i !== index);
    handleValueChanges(newValues);
  }

  const [prevValue, setPrevValue] = useState(value);
  if (prevValue !== value) {
    setPrevValue(value);
    setValues(value ?? []);
  }

  const displayLimit = 3;
  const displayedValues =
    showMoreIndicator && items ? values.slice(0, displayLimit) : values;
  const moreCount = values.length - displayLimit;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Autocomplete
          multiple
          freeSolo={!items?.length}
          options={items?.map((item) => item.value) || []}
          value={values}
          inputValue={inputValue}
          onInputChange={(_, newInputValue) => {
            if (!disabled) setInputValue(newInputValue);
          }}
          onChange={(_, newValue) => {
            if (!disabled) handleValueChanges(newValue as string[]);
          }}
          renderTags={() => null}
          renderInput={(params) => (
            <TextField
              {...params}
              error={error}
              fullWidth
              onBlur={onBlur}
              disabled={disabled}
              variant="outlined"
              slotProps={{
                input: {
                  ...params.InputProps,
                  style: {
                    borderRadius: "6px",
                    height: "45px",
                    display: "flex",
                    alignItems: "center",
                    padding: "0 14px",
                  },
                },
                htmlInput: {
                  ...params.inputProps,
                  style: {
                    padding: 0,
                  },
                },
              }}
              placeholder="Type and press enter"
              onKeyDown={(e) => {
                if (disabled) {
                  e.preventDefault();
                  return;
                }
                if (e.key === "Enter") {
                  e.preventDefault();
                  const trimmedValue = inputValue.trim();
                  if (
                    trimmedValue &&
                    (!items?.length ||
                      items.find((item) => item.value === trimmedValue))
                  ) {
                    addValue(trimmedValue);
                    setInputValue("");
                  }
                }
              }}
            />
          )}
          disableCloseOnSelect
        />
      </Grid>
      {values.length > 0 && (
        <Grid item xs={12}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {displayedValues.map((option, index) => (
              <Chip
                key={`${option}-${index}`}
                variant="outlined"
                label={
                  items?.find((item) => item.value === option)?.label || option
                }
                onDelete={disabled ? undefined : () => removeValue(index)}
              />
            ))}
            {showMoreIndicator && items && moreCount > 0 && (
              <Chip
                key="more"
                variant="outlined"
                label={`+ ${moreCount} more`}
              />
            )}
          </Box>
        </Grid>
      )}
    </Grid>
  );
}
